import React from "react";
import { motion } from "framer-motion";
import { Shield } from "lucide-react";

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, delay, ease: "easeOut" },
});

const RefundPolicy = () => {
    return (
        <div className="min-h-screen bg-white relative overflow-hidden">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-br from-[#00809D] via-[#006d87] to-[#005568] overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />

                <div className="relative z-10 pt-32 sm:pt-40 lg:pt-48 pb-24 sm:pb-28 lg:pb-32 px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md px-5 py-2 rounded-full mb-6 border border-white/20">
                            <Shield className="w-4 h-4 text-white/90" />
                            <span className="text-white/90 font-medium text-sm">Policies</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight tracking-tight">
                            Refund Policy
                        </h1>
                        <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                            Clear and transparent guidelines on our cancellation and refund process.
                        </p>
                    </motion.div>
                </div>

                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="white" />
                    </svg>
                </div>
            </div>

            {/* Content Section */}
            <div className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                <div className="max-w-4xl mx-auto text-gray-600">
                    <motion.div {...fadeUp()}>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Overview</h2>
                        <p className="mb-6">At Adsurg, we strive to ensure our clients are fully satisfied with our performance marketing and strategic advertising services. Given the nature of digital marketing services, this Refund Policy outlines the conditions under which refunds may or may not be granted.</p>

                        <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Non-Refundable Services</h2>
                        <p className="mb-6">Most of our services, including ad spend, setup fees, strategy consulting, and management fees, are non-refundable once the work has commenced. This is because significant time, resources, and third-party costs are incurred immediately upon initiating a campaign.</p>

                        <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility for Refunds</h2>
                        <p className="mb-4">Refunds may be considered in the following limited circumstances:</p>
                        <ul className="list-disc pl-6 mb-6">
                            <li>If you cancel services before any work has commenced or any ad spend has been utilized.</li>
                            <li>If there is an explicit error in billing on our part (e.g., duplicate charges).</li>
                        </ul>

                        <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cancellation Process</h2>
                        <p className="mb-6">
                            To request a cancellation or refund, please contact us via our <a href="/contact" className="text-[#00809D] font-semibold hover:underline">contact form</a> with your account details and the reason for your request. All requests will be reviewed by our team within 7-10 business days.
                        </p>

                        <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Rejection of Refunds</h2>
                        <p className="mb-6">We reserve the right to deny refund requests if the client has violated our Terms and Conditions, or if the campaign has already resulted in measurable deliverables or ad platform spend.</p>

                        <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Contact Us</h2>
                        <p className="mb-6">
                            If you have any questions about this Refund Policy, please contact our support team via our <a href="/contact" className="text-[#00809D] font-semibold hover:underline">contact form</a>.
                        </p>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default RefundPolicy;
